import { useState } from "react";
import { useQuestionario } from "../../../hooks/useFornecedores";
import { useNavigate } from "react-router-dom";
import { FaCommentDots } from "react-icons/fa";
import {
  IoSendOutline
} from "react-icons/io5";
import Alert from "../../shared/alert/Alert";
import Masks from "../../../utils/masks";
import Style from "./index.module.css";

const OptionGroup = ({ question, name, options, onChange, value, onFileChange }) => {
  return (
    <div className={Style["option-group"]}>
      <div className={Style["option-question"]}>{question}</div>
      <div className={Style["option-buttons"]}>
        {options.map(opt => {
          const isSelected = value === opt.value;
          return (
            <button
              key={opt.value}
              type="button"
              className={`${Style["option-btn"]} ${isSelected ? Style.selected : ""}`}
              onClick={() => onChange(name, opt.value)}
            >
              {opt.label}
            </button>
          );
        })}
      </div>

      {value === "S" && (
        <div className={Style["upload-field"]}>
          <input
            type="file"
            accept="application/pdf"
            onChange={e => onFileChange(name, e.target.files[0], parseInt(name.split("_")[1]))}
          />
        </div>
      )}
    </div>
  );
};

export const Questionario = (errors) => {
  const [form, setForm] = useState({});
  const [files, setFiles] = useState([]);
  const navigate = useNavigate();

  const { enviarQuestionario, isLoading } = useQuestionario();

  const handleChange = (name, value) => {
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = async (name, file, idx) => {
    if (!file) return;

    const isPdfByMime = file.type === "application/pdf";
    const isPdfByExt = file.name.toLowerCase().endsWith(".pdf");

    if (!isPdfByMime || !isPdfByExt) {
      await Alert({
        icon: "error",
        title: "Arquivo inválido",
        message: "Envie apenas arquivos PDF.",
        confirmButtonText: "OK",
        timer: 3500,
        showConfirmButton: true,
      });
      return;
    }

    const reader = new FileReader();

    reader.onload = (e) => {
      const arrayBuffer = e.target.result;
      const binary = new Uint8Array(arrayBuffer);

      setForm(prev => ({ ...prev, [name]: "S" }));

      setFiles(prev => {
        const filtered = prev.filter(f => f.idx !== idx);
        return [...filtered, { binary, idx, name }];
      });
    };

    reader.readAsArrayBuffer(file);
  };


  const handleSubmit = async (e) => {
    e.preventDefault();

    for (let idx = 0; idx < requisitosLegais.length; idx++) {
      const key = `reqLegal_${idx}`;
      if (form[key] === "S" && !files.some(f => f.idx === idx)) {
        await Alert({
          icon: "error",
          title: "Erro",
          confirmButtonText: "OK",
          showConfirmButton: true,
          message: `É obrigatório enviar um arquivo para a pergunta: "${requisitosLegais[idx]}".`
        });
        return;
      }
    }

    const confirmSubmit = await Alert({
      icon: "warning",
      title: "Confirmação",
      message: "Revisou todas as respostas? Confirme para finalizar o envio!",
      showConfirmButton: true,
      showCancelButton: true,
    });

    if (!confirmSubmit) return;

    try {
      const response = await enviarQuestionario(form, files);

      if (response.sucesso) {
        localStorage.setItem("user", JSON.stringify({ ...JSON.parse(localStorage.getItem("user") || "{}"), status: "OK" }));
        navigate("/home");

        await Alert({
          icon: "success",
          title: "Sucesso",
          confirmButtonText: "OK",
          showConfirmButton: true,
          message: response.docs.mensagem,
          timer: 3500,
          allowOutsideClick: false,
          allowEscapeKey: false,
          showCloseButton: false
        });

        window.location.reload();
      } else {
        await Alert({
          icon: "error",
          title: "Erro",
          message: response || "Falha ao salvar o questionário",
          showConfirmButton: true,
          confirmButtonText: "OK",
        });
      }
    } catch (error) {
      await Alert({
        icon: "error",
        title: "Erro",
        message: error.erro || "Falha ao salvar o questionário",
        showConfirmButton: true,
        confirmButtonText: "OK",
      });
    }
  };

  const requisitosLegais = [
    "Possui Alvará de funcionamento expedido pela Prefeitura?",
    "Possui Licença de Instalações do órgão ambiental?",
    "É um fornecedor local? (GRI 204-1)",
    "É um fornecedor sustentável? (GRI 308-1)",
    "Possui impacto social positivo? (GRI 414-1)",
    "Possui encarregado de dados e práticas de LGPD?"
  ];

  const requisitosQualidade = [
    "Possui Sistema de Gestão da Qualidade implementado?",
    "Realiza análise crítica das solicitações de compra?",
    "Possui instalações adequadas para produção?",
    "Faz inspeções e mantém registros de rastreabilidade?",
    "Possui equipamentos de medição com certificados de calibração?",
    "Realiza inspeções durante a produção e na entrega?",
    "Controla armazenagem, manuseio e entrega?",
    "Emite certificado de qualidade?",
    "Possui líderes de produção?",
  ];

  return (
    <form className={Style.formContainer} onSubmit={handleSubmit}>
      <h2>
        <FaCommentDots style={{ marginRight: '8px' }} />
        Questionário do Fornecedor
      </h2>
      <p>Responda o questionário para avaliarmos a sua empresa.</p>
      <br />

      {/* Requisitos Legais */}
      <section className={Style.card}>
        <h3>1 - Requisitos Legais e Outros</h3>
        <p style={{ fontSize: "10pt" }}>
          Selecione os requisitos legais ou outros critérios aplicáveis ao seu negócio. 
        </p>
        {requisitosLegais.map((q, idx) => (
          <OptionGroup
            key={idx}
            question={q}
            name={`reqLegal_${idx}`}
            options={[
              { label: "Sim", value: "S" },
              { label: "Não", value: "N" },
              { label: "N/A", value: "NA" }
            ]}
            onChange={handleChange}
            onFileChange={handleFileChange}
            value={form[`reqLegal_${idx}`] ?? "NA"}
          />
        ))}
      </section>

      {/* Requisitos de Qualidade */}
      <section className={Style.card}>
        <h3>2 - Controle e Gestão da Qualidade</h3>
        <div style={{ fontSize: "11pt" }}>
          <p>Selecione os itens de controle de gestão da qualidade aplicáveis ao seu negócio:</p>
          <ul style={{ listStyleType: "none", paddingLeft: 0, margin: 0, marginTop: 20 }}>
            <li>
              <strong>0 –</strong> Não existe documentação e a empresa não pratica
            </li>
            <li>
              <strong>1 –</strong> Existe somente documentação e a empresa pratica parcialmente
            </li>
            <li>
              <strong>2 –</strong> A empresa apenas pratica mas não possui documentação comprobatória
            </li>
            <li>
              <strong>3 –</strong> Existe documentação e a empresa pratica e tem como comprovar
            </li>
          </ul>
          <br />
        </div>

        {requisitosQualidade.map((q, idx) => (
          <OptionGroup
            key={idx}
            question={q}
            name={`qualidade_${idx}`}
            options={[
              { label: "0", value: "0" },
              { label: "1", value: "1" },
              { label: "2", value: "2" },
              { label: "3", value: "3" }
            ]}
            onChange={handleChange}
            value={form[`qualidade_${idx}`] ?? "0"}
          />
        ))}
      </section>

      {/* Lista de Clientes */}
      <section className={Style.card}>
        <h3>3 - Lista de Clientes</h3>
        <p style={{ fontSize: "10pt" }}>
          Informe seus três principais clientes.
        </p>

        {[1, 2, 3].map((index) => (
          <div
            key={index}
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "8px",
            }}
          >
            <div style={{ display: "flex", flexDirection: "column" }}>
              <input
                className={Style.input}
                type="text"
                name={`cliente${index}Razao`}
                placeholder={`Razão Social ${index}`}
                value={form[`cliente${index}Razao`] || ""}
                onChange={(e) => handleChange(e.target.name, e.target.value)}
              />

              {errors[`cliente${index}Razao`] && (
                <span className="error">
                  {errors[`cliente${index}Razao`]}
                </span>
              )}
            </div>

            <div style={{ display: "flex", flexDirection: "column" }}>
              <input
                className={Style.input}
                type="tel"
                name={`cliente${index}Telefone`}
                placeholder={`Telefone ${index}`}
                value={form[`cliente${index}Telefone`] || ""}
                onChange={(e) => {
                  let value = Masks.telefone(e.target.value);
                  handleChange(e.target.name, value);
                }}
              />
              {errors[`cliente${index}Telefone`] && (
                <span className="error">{errors[`cliente${index}Telefone`]}</span>
              )}
            </div>
          </div>
        ))}
      </section>

      <button
        type="submit"
        className={Style.btn}
        disabled={isLoading}
      >
        <IoSendOutline />
        {isLoading ? "Enviando..." : "Enviar Questionário"}
      </button>
    </form>
  );
};